<script setup lang="ts">
import { Modal } from 'ant-design-vue'
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { getAccountCurrent } from '../api/user/setting'
import { resolveAccountSections } from './account/sections'
import AccountOrderModal from './account/AccountOrderModal.vue'
import { useAuthStore } from '../stores/auth'
import { useFeatureStore } from '../stores/feature'
import { callTestAction, useTestActions } from '../composables/useTestAction'
import { useI18n } from '../composables/useI18n'

const props = defineProps<{ open: boolean; sections?: string[]; initialTab?: string }>()
const emit = defineEmits<{ 'update:open': [value: boolean] }>()

const auth = useAuthStore()
const feature = useFeatureStore()
const router = useRouter()
const { t } = useI18n()

// 未开启点数功能时，隐藏点数充值分节
const creditSections = ['recharge']
const sectionDefs = computed(() => {
  const list = resolveAccountSections(props.sections)
  return feature.creditEnabled ? list : list.filter((s) => !creditSections.includes(s.key))
})
const activeTab = ref('')
const orderVisible = ref(false)
const rechargeAgreementVisible = ref(false)

// Single source of truth for the signed-in account, shared with the panels below
// so switching tabs no longer re-requests /api/user/account/current each time.
const account = reactive({ username: '', email: '', phone: '', avatar: '' })

async function loadAccount() {
  try {
    const data = await getAccountCurrent()
    account.username = data.username
    account.email = data.email
    account.phone = data.phone || ''
    account.avatar = data.avatar || ''
  } catch {
    // ignore
  }
}

const activeSection = computed(
  () => sectionDefs.value.find((s) => s.key === activeTab.value) ?? sectionDefs.value[0],
)
// Only「基础信息」and「修改信息」read the account fields
const contentProps = computed(() => {
  const key = activeSection.value?.key
  if (key === 'account') {
    return {
      ...account,
      showCredit: sectionDefs.value.some((s) => s.key === 'recharge'),
      phoneEnabled: feature.phoneEnabled,
    }
  }
  if (key === 'info') return { ...account, phoneEnabled: feature.phoneEnabled }
  if (key === 'recharge') return { agreementOpen: rechargeAgreementVisible.value }
  return {}
})

const contentHandlers = computed(() => {
  const base: Record<string, (...args: any[]) => void> = {
    navigate: (key: string) => (activeTab.value = key),
    logout: handleLogout,
    // v-on 对象里的 key 会被 toHandlerKey 转成 onXxx，必须与 emit 的事件名大小写一致
    openOrder: () => (orderVisible.value = true),
    'update:agreementOpen': (value: boolean) => (rechargeAgreementVisible.value = value),
  }
  if (activeSection.value?.key !== 'info') return base
  return {
    ...base,
    'update:avatar': (value: string) => (account.avatar = value),
    'update:username': (value: string) => (account.username = value),
    'update:email': (value: string) => (account.email = value),
    'update:phone': (value: string) => (account.phone = value),
  }
})

watch(
  () => props.open,
  (open) => {
    if (!open) {
      orderVisible.value = false
      rechargeAgreementVisible.value = false
      return
    }
    void loadAccount()
    // 指定分节（如从首页钱包进入「点数充值」）优先，其次保留上次分节
    if (props.initialTab && sectionDefs.value.some((s) => s.key === props.initialTab)) {
      activeTab.value = props.initialTab
      return
    }
    if (!sectionDefs.value.some((s) => s.key === activeTab.value)) {
      activeTab.value = sectionDefs.value[0]?.key || ''
    }
  },
  { immediate: true },
)

useTestActions({
  'shell.account.open.prepare': () => {
    emit('update:open', true)
  },
  'shell.account.open.cleanup': () => {
    emit('update:open', false)
  },
  'shell.account.recharge.prepare': () => {
    activeTab.value = 'recharge'
    orderVisible.value = false
    emit('update:open', true)
  },
  'shell.account.recharge.cleanup': () => {
    emit('update:open', false)
  },
  'shell.account.order.prepare': () => {
    activeTab.value = 'recharge'
    orderVisible.value = true
    emit('update:open', true)
  },
  'shell.account.order.cleanup': () => {
    emit('update:open', false)
    orderVisible.value = false
  },
  'shell.account.rechargeAgreement.prepare': () => {
    activeTab.value = 'recharge'
    rechargeAgreementVisible.value = true
    emit('update:open', true)
  },
  'shell.account.rechargeAgreement.cleanup': () => {
    emit('update:open', false)
    rechargeAgreementVisible.value = false
  },
  'shell.account.rechargeQr.prepare': async () => {
    activeTab.value = 'recharge'
    orderVisible.value = false
    emit('update:open', true)
    await nextTick()
    await callTestAction('shell.account.rechargeQr.open')
  },
  'shell.account.rechargeQr.cleanup': async () => {
    try {
      await callTestAction('shell.account.rechargeQr.close')
    } catch {
      // ignore
    }
    emit('update:open', false)
  },
})

function handleLogout() {
  Modal.confirm({
    title: t('shell.logout.title'),
    content: t('shell.logout.desc'),
    okText: t('shell.logout.ok'),
    cancelText: t('common.cancel'),
    okButtonProps: { danger: true },
    onOk: () => {
      auth.logout()
      router.push('/login')
    },
  })
}
</script>

<template>
  <a-modal
    width="min(820px, 95vw)"
    :open="open"
    :footer="null"
    :keyboard="true"
    :mask-closable="true"
    @cancel="emit('update:open', false)"
  >
    <div class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{{ t('shell.account.title') }}</div>
    <div class="flex flex-col gap-3 sm:flex-row sm:gap-5">
      <!-- 左侧标签导航 -->
      <nav
        v-if="sectionDefs.length > 1"
        class="flex shrink-0 gap-1 overflow-x-auto sm:w-28 sm:flex-col sm:overflow-visible"
      >
        <div
          v-for="s in sectionDefs"
          :key="s.key"
          class="flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition-all duration-200 whitespace-nowrap"
          :class="activeTab === s.key
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:text-gray-700 dark:hover:text-gray-200'"
          :data-testid="'account-tab-' + s.key"
          @click="activeTab = s.key"
        >
          <component :is="s.icon" class="w-4 h-4 shrink-0" aria-hidden="true" />
          <span class="text-sm">{{ t(s.labelKey) }}</span>
        </div>
      </nav>

      <!-- 内容面板：高度随内容自适应，超出视口时由弹窗外层整体滚动。
           设最小高度（--modal-height-min），避免「基础信息」等内容较少时弹窗塌缩成细条。
           交叉淡入淡出（不用 out-in）：退出的面板绝对定位，不再占位，
           避免切换瞬间内容为空导致高度塌缩闪烁 -->
      <div class="relative flex flex-1 flex-col min-w-0 pr-1 min-h-[var(--modal-height-min)]">
        <Transition name="account-pane">
          <component
            :is="activeSection?.component"
            :key="activeSection?.key"
            class="flex-1"
            v-bind="contentProps"
            v-on="contentHandlers"
          />
        </Transition>
      </div>
    </div>

    <AccountOrderModal v-model:open="orderVisible" />
  </a-modal>
</template>

<style scoped>
.account-pane-enter-active,
.account-pane-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
/* Leaving pane is taken out of flow so the entering pane sets the height in the
   same frame — no empty frame that collapses the dialog */
.account-pane-leave-active {
  position: absolute;
  inset: 0 0 auto 0;
}
.account-pane-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.account-pane-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
